import {
  DialogsStack,
  NotificationsTicker,
  Revision,
  ScrollToTop,
} from "@neru/base/components";
import { mainNavigationConfig } from "@neru/base/config";
import {
  getActiveMenuItem,
  MAIN_NAVIGATION_HEIGHT,
  MainNavigation,
  MENU_KEYS,
} from "@neru/base/features/MainNavigation";
import {
  SecureRouteWithCookie,
  SecureRouteWithToken,
} from "@neru/base/features/Security";
import { useCurrentAccount } from "@neru/base/hooks";
import { componentLoader, MFA } from "@neru/base/utils";
import { Banner, Column, Layout, Spinner } from "@neru/ui";
import { lazy, Suspense, useEffect } from "react";
import { Outlet, Route, Routes, useLocation } from "react-router-dom";

import { shouldTrack, trackPageView } from "./utils/AdobeAnalytics";
import { config } from "./utils/config";

const Home = lazy(() => componentLoader(() => import("./features/Home/Home")));
const Product = lazy(() =>
  componentLoader(() => import("./features/Product/Product")),
);
const ProductVersions = lazy(() =>
  componentLoader(() => import("./features/Product/ProductVersions")),
);
const ProductClients = lazy(() =>
  componentLoader(() => import("./features/Product/ProductClients")),
);
const PublishPage = lazy(() =>
  componentLoader(() => import("./features/Product/PublishPage")),
);
const AccountOverview = lazy(() =>
  componentLoader(
    () => import("@neru/base/features/AccountOverview/AccountOverview"),
  ),
);
const PlansAndPricing = lazy(() =>
  componentLoader(
    () => import("@neru/base/features/PlansAndPricing/PlansAndPricing"),
  ),
);

const DeployedProducts = lazy(() =>
  componentLoader(
    () => import("@neru/base/features/DeployedProducts/DeployedProducts"),
  ),
);
const DeployedProduct = lazy(() =>
  componentLoader(
    () =>
      import(
        "@neru/base/features/DeployedProducts/DeployedProduct/DeployedProduct"
      ),
  ),
);

const Instances = lazy(() =>
  componentLoader(() => import("@neru/base/features/Instances/Instances")),
);
const Instance = lazy(() =>
  componentLoader(() => import("@neru/base/features/Instances/Instance")),
);

const Workspaces = lazy(() =>
  componentLoader(() => import("@neru/base/features/Workspaces/Workspaces")),
);
const Vulnerabilities = lazy(() =>
  componentLoader(
    () => import("@neru/base/features/Vulnerabilities/Vulnerabilities"),
  ),
);
const CreateWorkspace = lazy(() =>
  componentLoader(
    () => import("@neru/base/features/CreateWorkspace/CreateWorkspace"),
  ),
);
const Workspace = lazy(() =>
  componentLoader(() => import("@neru/base/features/Workspaces/Workspace")),
);

const App = ({ hasPortalSession }: { hasPortalSession: boolean }) => {
  const SecureRoute = config.isMFA
    ? SecureRouteWithCookie
    : SecureRouteWithToken;

  const location = useLocation();
  const { pathname } = location;
  const { isBanned } = useCurrentAccount();

  useEffect(() => {
    if (shouldTrack(pathname)) {
      trackPageView();
      MFA.notifyPageReady();
    }
  }, [pathname]);

  return (
    <Layout className="p-0">
      <Column className="grow" id="container">
        <MainNavigation
          active={getActiveMenuItem(pathname) ?? MENU_KEYS.market}
          logo={
            <a href={mainNavigationConfig.urlMarket}>
              <div className="relative my-2 h-6 w-6">
                <div
                  className={`
                    t-[2px] l-0 absolute h-[8.5px] w-[8.5px] rotate-45
                    rounded-xs bg-[#facc4b]
                  `}
                ></div>
                <div
                  className={`
                    t-[3px] absolute left-3 h-2 w-2 rotate-16 rounded-xs
                    bg-[#fb8fd8]
                  `}
                ></div>
                <div
                  className={`
                    absolute bottom-0 left-0 h-[11px] w-[11px] rounded-sm
                    bg-[#9941ff]
                  `}
                ></div>
                <div
                  className={`
                    absolute bottom-px left-[13px] h-[9px] w-[9px] rotate-30
                    rounded-xs bg-[#fa7454]
                  `}
                ></div>
              </div>
            </a>
          }
          theme={config.isMFA ? "light" : "dark"}
          withAccountSelector
          withRegionSelector
          withSignIn={!config.isMFA && !config.skipAuth}
        />

        <Column
          className="z-0 box-border grow basis-0 justify-start"
          id="main-content"
          style={{ marginTop: MAIN_NAVIGATION_HEIGHT }}
        >
          {isBanned ? (
            <Banner
              connotation="alert"
              data-testid="alert-blocked-banner"
              message="This account has been blocked. Please contact support."
              open
            />
          ) : (
            <Suspense fallback={<Spinner />}>
              <Routes>
                <Route
                  element={
                    config.skipAuth ? (
                      <Outlet />
                    ) : (
                      <SecureRoute
                        hasPortalSession={
                          config.isMFA ? hasPortalSession : undefined
                        }
                      />
                    )
                  }
                >
                  <Route element={<AccountOverview />} path="/overview" />
                  <Route
                    element={<PlansAndPricing />}
                    path="/plans-and-pricing"
                  />
                  <Route element={<Instances />} path="/instances" />
                  <Route element={<Workspaces />} path="/workspaces" />
                  <Route
                    element={<CreateWorkspace />}
                    path="/workspaces/create"
                  />
                  <Route
                    element={<Workspace />}
                    path="/workspaces/:workspaceId"
                  />
                  <Route
                    element={<Instance />}
                    path="/instances/:instanceServiceName"
                  />
                  <Route
                    element={<DeployedProducts />}
                    path="/deployed-products"
                  />
                  <Route
                    element={<Vulnerabilities />}
                    path="/vulnerabilities"
                  />
                  <Route
                    element={<DeployedProduct />}
                    path="/deployed-products/:productId"
                  />
                  <Route element={<PublishPage />} path="/publish" />
                  <Route element={<PublishPage />} path="/publish/:productId" />
                  <Route
                    element={<PublishPage />}
                    path="/:productId/versions/:versionId"
                  />
                  <Route
                    element={<ProductVersions />}
                    path="/:productId/versions"
                  />
                  <Route
                    element={<ProductClients />}
                    path="/:productId/clients"
                  />
                </Route>

                <Route
                  element={hasPortalSession ? <SecureRoute /> : <Outlet />}
                >
                  <Route element={<Home />} path="/" />
                  <Route element={<Product />} path="/:productId" />
                  <Route
                    element={<Product documentation />}
                    path="/:productId/documentation"
                  />
                  <Route
                    element={<Product releases />}
                    path="/:productId/releases"
                  />
                </Route>
              </Routes>
            </Suspense>
          )}
        </Column>
      </Column>

      <div
        className="flex grow basis-[100vh]"
        id="documentation"
        style={{ display: "none" }}
      ></div>

      <DialogsStack />
      <NotificationsTicker />
      <Revision />
      <ScrollToTop />
    </Layout>
  );
};

export default App;
